/**
 * The "Featured" group on the Tasks model picker (decision D3).
 *
 * Approved by Lance on 2026-09-29, as drafted in
 * local-campaign-oversight/FEATURED_MODELS_DRAFT_D3_20260929.md (nine models,
 * creative-first, each with a caution; the Aion 3.5 pair replaces Aion 3.0).
 * GPT-6.1 Sol and Sonnet 5.5 added as optional choices at Lance's request
 * on 2026-09-30. Existing order and default preferences are preserved.
 *
 * Each entry names a model exactly as the price book does. An entry whose
 * model is not in the current options (not priced, or no key for its provider)
 * is simply not shown.
 */

export interface FeaturedModel {
  provider: string;
  model: string;
  /** One line: what it is good for, in customer words. */
  bestFor: string;
  /** One line: the honest caveat. */
  caution: string;
}

export const FEATURED_MODELS: ReadonlyArray<FeaturedModel> = [
  {
    provider: 'openrouter', model: 'openai/gpt-6-luna',
    bestFor: 'Fast and very cheap. Good for first drafts, summaries, outlines and structured edits.',
    caution: 'A plainer voice than premium writing models.',
  },
  {
    provider: 'openrouter', model: 'anthropic/claude-sonnet-5',
    bestFor: 'Careful long-form prose, revision passes and following detailed instructions.',
    caution: 'Premium price; more than you need for quick drafts.',
  },
  {
    provider: 'openrouter', model: 'anthropic/claude-opus-5.5',
    bestFor: 'Top-tier judgement for final passes and hard structural problems.',
    caution: 'The most expensive option here, and slower.',
  },
  {
    provider: 'openrouter', model: 'openai/gpt-6-sol',
    bestFor: 'A high-end all-rounder: strong reasoning and writing without the top price.',
    caution: 'Long prompts cost more (tiered pricing).',
  },
  {
    provider: 'openrouter', model: 'google/gemini-4-argon',
    bestFor: 'Optional long-context reasoning, coding and multimodal analysis.',
    caution: 'Paid account access required; not yet evaluated by Auteur. Text output only.',
  },
  {
    provider: 'openrouter', model: 'google/gemini-3.8-flash',
    bestFor: 'Reads a whole manuscript at once (1M tokens): fast summaries and continuity notes across long material.',
    caution: 'A less distinctive prose voice.',
  },
  {
    provider: 'openrouter', model: 'deepseek/deepseek-v4.1-flash',
    bestFor: 'Low-cost analysis, research notes and checking facts against your notes.',
    caution: 'In our checks it gave fewer usable answers than peers, so review its output.',
  },
  {
    provider: 'openrouter', model: 'moonshotai/kimi-k3',
    bestFor: 'Big-picture structure and planning across long material.',
    caution: 'Premium price.',
  },
  {
    provider: 'openrouter', model: 'aion-labs/aion-3.5',
    bestFor: 'Built for storytelling and character voice.',
    caution: 'A smaller memory (262k tokens) than the 1M-token models.',
  },
  {
    provider: 'openrouter', model: 'aion-labs/aion-3.5-mini',
    bestFor: 'Affordable story variants and dialogue exploration.',
    caution: 'A smaller memory (262k tokens) than the 1M-token models.',
  },
  {
    provider: 'openrouter', model: 'openai/gpt-6.1-sol',
    bestFor: 'Optional reasoning and coding model, with a choice of thinking level.',
    caution: 'Premium price; not yet tested by Auteur. Long prompts cost more.',
  },
  {
    provider: 'openrouter', model: 'anthropic/claude-sonnet-5.5',
    bestFor: 'Optional prose, coding and revision model, with a choice of thinking level.',
    caution: 'Not yet tested by Auteur. More thinking can take longer and cost more.',
  },
];

/**
 * Where a new task starts when the person has not told us what they plan to
 * do yet (Lance, 2026-09-29: GPT-6 Luna, the cheapest safe start). The first
 * one present in the chosen key's models wins; otherwise the picker falls back
 * to its first visible option.
 */
export const DEFAULT_MODELS: ReadonlyArray<{ provider: string; model: string }> = [
  { provider: 'openrouter', model: 'openai/gpt-6-luna' },
  { provider: 'openai', model: 'gpt-6-luna' },
];
